/**
 * CR316: Search Suggestions Configuration Section
 * 
 * Controls for search suggestion behavior.
 * CRITICAL: Suggestions use card-visible fields only (no terms/fine print).
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lightbulb } from "lucide-react";
import type { SearchSuggestionsConfig } from "@/lib/layout-studio/types";
import { DEFAULT_SEARCH_SUGGESTIONS_CONFIG } from "@/lib/layout-studio/types";

interface SearchSuggestionsSectionProps {
  config: SearchSuggestionsConfig;
  onChange: (config: SearchSuggestionsConfig) => void;
}

const SUGGESTION_TYPE_LABELS: Record<keyof SearchSuggestionsConfig["typesEnabled"], string> = {
  merchant: "Merchant",
  brand: "Brand",
  category: "Category",
  title: "Deal Title",
};

export function SearchSuggestionsSection({ config, onChange }: SearchSuggestionsSectionProps) {
  const mergedConfig = { ...DEFAULT_SEARCH_SUGGESTIONS_CONFIG, ...config };

  const handleToggle = (enabled: boolean) => {
    onChange({ ...mergedConfig, enabled });
  };

  const handleMinCharsChange = (value: number) => {
    onChange({ ...mergedConfig, minChars: value });
  };

  const handleMaxItemsChange = (value: number) => {
    onChange({ ...mergedConfig, maxItems: value });
  };

  const handleMatchModeChange = (matchMode: SearchSuggestionsConfig["matchMode"]) => {
    onChange({ ...mergedConfig, matchMode });
  };

  const handleTypeToggle = (type: keyof SearchSuggestionsConfig["typesEnabled"], enabled: boolean) => {
    // Ensure at least one type remains enabled
    const newTypes = { ...mergedConfig.typesEnabled, [type]: enabled };
    if (!Object.values(newTypes).some(Boolean)) return;
    onChange({ ...mergedConfig, typesEnabled: newTypes });
  };

  const handleShowTypeLabelToggle = (showTypeLabel: boolean) => {
    onChange({ ...mergedConfig, showTypeLabel });
  };

  const handleShowCountsToggle = (showCounts: boolean) => {
    onChange({ ...mergedConfig, showCounts });
  };

  const handleDedupeToggle = (dedupe: boolean) => {
    onChange({ ...mergedConfig, dedupe });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Lightbulb className="h-4 w-4" />
            Search Suggestions
          </CardTitle>
          <Switch
            checked={mergedConfig.enabled}
            onCheckedChange={handleToggle}
            aria-label="Enable search suggestions"
          />
        </div>
        <CardDescription>
          Show suggestions as users type in the search bar.
        </CardDescription>
      </CardHeader>
      {mergedConfig.enabled && (
        <CardContent className="space-y-4">
          {/* Min Characters */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Min Characters</Label>
              <span className="text-[10px] text-muted-foreground tabular-nums">
                {mergedConfig.minChars}
              </span>
            </div>
            <Slider
              value={[mergedConfig.minChars]}
              onValueChange={([v]) => handleMinCharsChange(v)}
              min={1}
              max={5}
              step={1}
              className="w-full"
            />
          </div>

          {/* Max Items */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Max Suggestions</Label>
              <span className="text-[10px] text-muted-foreground tabular-nums">
                {mergedConfig.maxItems}
              </span>
            </div>
            <Slider
              value={[mergedConfig.maxItems]}
              onValueChange={([v]) => handleMaxItemsChange(v)}
              min={3}
              max={15}
              step={1}
              className="w-full"
            />
          </div>

          {/* Match Mode */}
          <div className="space-y-2">
            <Label className="text-xs">Match Mode</Label>
            <Select
              value={mergedConfig.matchMode}
              onValueChange={(v) => handleMatchModeChange(v as SearchSuggestionsConfig["matchMode"])}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="starts_with_then_contains">Starts with, then contains</SelectItem>
                <SelectItem value="starts_with">Starts with only</SelectItem>
                <SelectItem value="contains">Contains anywhere</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Suggestion Types */}
          <div className="space-y-2">
            <Label className="text-xs">Suggestion Types</Label>
            <div className="flex flex-wrap gap-3">
              {(Object.keys(SUGGESTION_TYPE_LABELS) as (keyof SearchSuggestionsConfig["typesEnabled"])[]).map((type) => (
                <div key={type} className="flex items-center space-x-2">
                  <Checkbox
                    id={`suggest-${type}`}
                    checked={mergedConfig.typesEnabled[type]}
                    onCheckedChange={(v) => handleTypeToggle(type, !!v)}
                    disabled={
                      Object.values(mergedConfig.typesEnabled).filter(Boolean).length === 1 &&
                      mergedConfig.typesEnabled[type]
                    }
                  />
                  <Label htmlFor={`suggest-${type}`} className="text-xs cursor-pointer">
                    {SUGGESTION_TYPE_LABELS[type]}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Display Options */}
          <div className="space-y-3 pt-2 border-t">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Show Type Label</Label>
              <Switch
                checked={mergedConfig.showTypeLabel}
                onCheckedChange={handleShowTypeLabelToggle}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Show Counts</Label>
              <Switch
                checked={mergedConfig.showCounts}
                onCheckedChange={handleShowCountsToggle}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Dedupe Suggestions</Label>
              <Switch
                checked={mergedConfig.dedupe}
                onCheckedChange={handleDedupeToggle}
              />
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
